import { useCallback, useState, useEffect } from 'react'

import { useNavigate } from 'react-router-dom'
import { useWallets } from '@privy-io/react-auth'
import { useAppLogin } from 'hooks/useAppLogin'
import { Box } from 'components'

import { useAuth } from 'store/auth/hooks'

import { ROUTES, getTeamDetailRoute, getProfileDetailRoute, getGameDetailRoute } from 'settings/constants'
import DefaultAvatar from 'assets/avatar.webp'
import usePrivyHook from 'hooks/usePrivyHooks'
import { getAxiosInstance } from 'services/api'
import { UpcomingMatchesList } from './widgets'
import { track } from 'utils/analytics'
import './RightSideNav.css'

// Get download URL based on OS
const getDownloadUrl = () => {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('win')) {
    return 'https://cdn.crabnebula.app/asset/01KD67R3C7SN0YQJ1PY9ZE3583' // Windows
  }
  return 'https://cdn.crabnebula.app/asset/01KD67RKHMWBZWT3YVMJ69GS98' // Linux
}

export const RightSideNav = () => {
  const { ready, wallets } = useWallets()
  const navigate = useNavigate()
  const downloadUrl = getDownloadUrl()
  const { signInUser, user: currentUser, followUser: followUserAction } = useAuth()
  const { authenticated, user } = usePrivyHook()

  // Suggested users state
  const [suggestedUsers, setSuggestedUsers] = useState<any[]>([])
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)

  // Local optimistic follow state - tracks users we've followed/unfollowed in this session
  const [optimisticFollows, setOptimisticFollows] = useState<Set<string>>(new Set())
  const [optimisticUnfollows, setOptimisticUnfollows] = useState<Set<string>>(new Set())

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null)

  // Unified search with debouncing
  const performSearch = async (query: string) => {
    if (query.trim().length < 2) {
      setSearchResults(null)
      setShowResults(false)
      return
    }

    setIsSearching(true)

    try {
      const api = getAxiosInstance()
      const response = await api.get(`/search?q=${encodeURIComponent(query)}&limit=5`)

      if (response.data.success) {
        setSearchResults(response.data.results)
        setShowResults(true)
        const results = response.data.results
        track('search_executed', { query, result_count: (results.users?.length || 0) + (results.teams?.length || 0) + (results.games?.length || 0) })
      }
    } catch (error) {
      console.error('Search error:', error)
      setSearchResults(null)
    } finally {
      setIsSearching(false)
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)

    // Clear existing timer
    if (searchTimer) {
      clearTimeout(searchTimer)
    }

    if (value.trim().length < 2) {
      setSearchResults(null)
      setShowResults(false)
      return
    }

    // Set new timer - debounce 300ms
    const timer = setTimeout(() => {
      performSearch(value)
    }, 300)

    setSearchTimer(timer)
  }

  // Fetch suggested users to follow
  const fetchSuggestedUsers = useCallback(async () => {
    if (!authenticated) return

    setIsLoadingSuggestions(true)
    try {
      const api = getAxiosInstance()
      const response = await api.get('/users/suggestions?limit=5')
      if (response.data.success) {
        setSuggestedUsers(response.data.users)
      }
    } catch (error) {
      console.error('Error fetching suggested users:', error)
    } finally {
      setIsLoadingSuggestions(false)
    }
  }, [authenticated])

  // Fetch suggestions on mount when authenticated
  useEffect(() => {
    if (authenticated) {
      fetchSuggestedUsers()
    }
  }, [authenticated, fetchSuggestedUsers])

  // Helper function to check if a user is followed (combines server state + optimistic state)
  const isUserFollowed = (userId: string): boolean => {
    // If we optimistically followed them, they're followed
    if (optimisticFollows.has(userId)) return true
    // If we optimistically unfollowed them, they're not followed
    if (optimisticUnfollows.has(userId)) return false
    // Otherwise check the server state
    return currentUser?.followings?.includes(userId) || false
  }

  // Handle follow/unfollow user toggle
  const handleFollowUser = (userId: string) => {
    const currentlyFollowed = isUserFollowed(userId)
    track(currentlyFollowed ? 'user_unfollowed' : 'user_followed', { target_user_id: userId })

    if (currentlyFollowed) {
      // Optimistically unfollow
      setOptimisticFollows(prev => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
      setOptimisticUnfollows(prev => new Set(prev).add(userId))
    } else {
      // Optimistically follow
      setOptimisticUnfollows(prev => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
      setOptimisticFollows(prev => new Set(prev).add(userId))
    }

    // Call the API via auth store action
    followUserAction(userId)
  }

  // Get the embedded wallet first, fallback to first connected wallet
  const embeddedWallet = wallets.find(w => w.connectorType === 'embedded')
  const walletAddress = embeddedWallet?.address || wallets[0]?.address || '0x0'

  const { login } = useAppLogin({
    onComplete: (params) => {
      if (!params || !params.user) {
        console.error('❌ User is undefined in login callback')
        return
      }

      const { user, isNewUser } = params
      console.log('✅ Login completed:', { user, isNewUser })

      const { id, email } = user
      const userEmail = email?.address || ''

      // Get wallet address at time of callback (might still be initializing)
      const currentEmbeddedWallet = wallets.find(w => w.connectorType === 'embedded')
      const currentWalletAddress = currentEmbeddedWallet?.address || wallets[0]?.address || '0x0'

      if (id) {
        signInUser({
          id,
          email: userEmail,
          isNewUser: isNewUser || false,
          accountAddress: currentWalletAddress,
        })
      }
    },
    onError: (error) => {
      console.log('error is here', error)
    },
  })

  // console.log('athenticated', authenticated)

  return (
    <Box className="right-side-nav w-[350px] my-4 mr-4 space-y-4" col>
      <Box className="fixed w-[350px] z-40">
        <div className="rsn-search-container">
          <div className="rsn-search-input-wrapper">
            <svg className="rsn-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              className="rsn-search-input"
              placeholder="Search Cyberlete"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => searchQuery.trim().length >= 2 && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
            />
            {!searchQuery && <span className="rsn-search-hint">⌘K</span>}
          </div>

          {/* Search Results Dropdown */}
          {showResults && searchResults && (
            <div className="rsn-search-results absolute w-full mt-2 max-h-[500px] overflow-y-auto">
              {isSearching ? (
                <div className="p-4 text-center text-gray-400">Searching...</div>
              ) : (
                <>
                  {/* Users */}
                  {searchResults.users && searchResults.users.length > 0 && (
                    <div>
                      <div className="search-category-title">Users</div>
                      {searchResults.users.map((user: any) => (
                        <div
                          key={user._id}
                          onClick={() => {
                            track('search_result_clicked', { type: 'user', result_id: user._id })
                            navigate(`/profile/${user.username}`)
                            setShowResults(false)
                            setSearchQuery('')
                          }}
                          className="search-item flex items-center gap-3 cursor-pointer"
                        >
                          <img
                            src={user.avatar?.url || DefaultAvatar}
                            alt={user.username}
                            className="w-10 h-10 rounded-full object-cover border border-white/10"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.onerror = null
                              target.src = DefaultAvatar
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="search-item-name truncate">
                              {user.firstname} {user.lastname}
                            </div>
                            <div className="search-item-meta truncate">@{user.username}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Teams */}
                  {searchResults.teams && searchResults.teams.length > 0 && (
                    <div className="border-t border-white/5">
                      <div className="search-category-title">Teams</div>
                      {searchResults.teams.map((team: any) => (
                        <div
                          key={team._id}
                          onClick={() => {
                            track('search_result_clicked', { type: 'team', result_id: team._id })
                            navigate(getTeamDetailRoute(team._id))
                            setShowResults(false)
                            setSearchQuery('')
                          }}
                          className="search-item flex items-center gap-3 cursor-pointer"
                        >
                          <img
                            src={team.avatar?.url || DefaultAvatar}
                            alt={team.team_name}
                            className="w-10 h-10 rounded-lg object-cover border border-white/10"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.onerror = null
                              target.src = DefaultAvatar
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="search-item-name truncate">{team.team_name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Games/Communities */}
                  {searchResults.games && searchResults.games.length > 0 && (
                    <div className="border-t border-white/5">
                      <div className="search-category-title">Games</div>
                      {searchResults.games.map((game: any) => (
                        <div
                          key={game._id}
                          onClick={() => {
                            track('search_result_clicked', { type: 'game', result_id: game._id })
                            navigate(getGameDetailRoute(game._id))
                            setShowResults(false)
                            setSearchQuery('')
                          }}
                          className="search-item flex items-center gap-3 cursor-pointer"
                        >
                          <img
                            src={game.avatar?.url || DefaultAvatar}
                            alt={game.name}
                            className="w-10 h-10 rounded-lg object-cover border border-white/10"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.onerror = null
                              target.src = DefaultAvatar
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="search-item-name truncate">{game.name}</div>
                            <div className="search-item-meta truncate line-clamp-1">{game.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tournaments */}
                  {searchResults.tournaments && searchResults.tournaments.length > 0 && (
                    <div className="border-t border-white/5">
                      <div className="search-category-title">Tournaments</div>
                      {searchResults.tournaments.map((tournament: any) => (
                        <div
                          key={tournament._id}
                          onClick={() => {
                            navigate(`/tournament/${tournament._id}`)
                            setShowResults(false)
                            setSearchQuery('')
                          }}
                          className="search-item flex items-center gap-3 cursor-pointer"
                        >
                          <img
                            src={tournament.game?.avatar?.url || DefaultAvatar}
                            alt={tournament.tournament_name}
                            className="w-10 h-10 rounded-lg object-cover border border-white/10"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.onerror = null
                              target.src = DefaultAvatar
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="search-item-name truncate">{tournament.tournament_name}</div>
                            <div className="search-item-meta truncate">
                              {tournament.game?.name} • ${tournament.prize_pool}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No results */}
                  {searchResults.users?.length === 0 &&
                   searchResults.teams?.length === 0 &&
                   searchResults.games?.length === 0 &&
                   searchResults.tournaments?.length === 0 && (
                    <div className="p-4 text-center text-gray-400">No results found</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Box>
      <Box className="h-8" />
      <a
        href={downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="rsn-download-btn"
        onClick={() => track('nav_desktop_app_clicked')}
      >
        Download Desktop App
      </a>
      <a
        href="https://discord.gg/3Vjjyrvbgp"
        target="_blank"
        rel="noopener noreferrer"
        className="rsn-discord-btn"
      >
        Join Discord
      </a>
      {/* Slots for page-specific content (e.g. the tournament join banner +
          timeline), injected via portal. Sit below Join Discord, above the
          Gamers-To-Follow card. Hidden when empty so they add no spacing on
          other pages. */}
      <div id="rsn-top-slot" className="empty:hidden" />
      <div id="rsn-secondary-slot" className="empty:hidden" />
      {/* Sign Up Card - Arena Themed */}
      {ready && !authenticated ? (
        <div className="rsn-arena-card rsn-signup-card">
          <h3 className="rsn-arena-card-title">New to Cyberlete?</h3>
          <p className="rsn-signup-text">
            Sign up now to get your own customized content.
          </p>
          <button className="rsn-signup-btn" onClick={login}>
            Sign up
          </button>
          <button className="rsn-login-link" onClick={login}>
            Already a member? Log in
          </button>
        </div>
      ) : null}

      {/* Upcoming Matches — compact Hub-style list (replaces old 309-line widget) */}
      {authenticated && <UpcomingMatchesList />}

      {/* Gamers To Follow - Arena Themed */}
      {authenticated && suggestedUsers.length > 0 && (
        <div className="rsn-arena-card rsn-gamers-card">
          <div className="rsn-arena-card-header">
            <h3 className="rsn-arena-card-title">Gamers To Follow</h3>
          </div>
          <div className="rsn-arena-card-body">
            {suggestedUsers.map((suggestedUser) => {
              const isFollowed = isUserFollowed(suggestedUser._id)

              return (
                <div key={suggestedUser._id} className="rsn-gamer-item">
                  <button
                    className="rsn-gamer-info"
                    onClick={() => navigate(getProfileDetailRoute(suggestedUser.username))}
                    aria-label={`View profile of ${suggestedUser.username}`}
                  >
                    <img
                      className="rsn-gamer-avatar"
                      src={suggestedUser.avatar?.url || DefaultAvatar}
                      alt=""
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.onerror = null
                        target.src = DefaultAvatar
                      }}
                    />
                    <div className="rsn-gamer-details">
                      <div className="rsn-gamer-name">
                        {suggestedUser.firstname || suggestedUser.lastname
                          ? `${suggestedUser.firstname || ''} ${suggestedUser.lastname || ''}`.trim()
                          : suggestedUser.username}
                      </div>
                      <div className="rsn-gamer-username">
                        @{suggestedUser.username}
                      </div>
                    </div>
                  </button>
                  <button
                    className={`rsn-follow-btn ${isFollowed ? 'following' : 'follow'}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleFollowUser(suggestedUser._id)
                    }}
                  >
                    {isFollowed ? 'Following' : 'Follow'}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Logged-in sidebar footer — only the About link surfaces here. The
          full set of legal links (ToS / Privacy / Whitepaper / Etherscan)
          live in the public footer, not in the in-app sidebar. */}
      <div className="rsn-quick-links">
        <a
          href="https://documents.cyberlete.net"
          target="_blank"
          rel="noopener noreferrer"
          className="rsn-quick-link"
        >
          About Cyberlete
        </a>
      </div>
    </Box>
  )
}
